import { Axios } from 'utils'
import { TGetUser, User } from 'types/models'
import { IFilterParams } from 'types/models/FilterSort'
import { GenericResult, ListPaginationParams, ListResponseType } from './types'

export const useUserService = () => {
  const getUsers = async (
    filter_params?: IFilterParams,
    pagination?: ListPaginationParams
  ) => {
    try {
      const response = await Axios.post<ListResponseType<TGetUser>>(
        `/users/filters`,
        { filter_params: filter_params },
        {
          params: pagination
            ? {
                offset: pagination.currentPage * pagination.itemsPerPage,
                limit: pagination.itemsPerPage,
              }
            : undefined,
        }
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const createUser = async (data: User) => {
    try {
      const response = await Axios.post<ListResponseType<User>>('/users', data)
      return response?.data
    } catch (error: unknown) {
      // Return the original Axios error to preserve response data
      throw error
    }
  }

  const updateUser = async (data: User) => {
    try {
      const response = await Axios.put<ListResponseType<User>>(
        `/users/${data.id}`,
        data
      )
      return response?.data
    } catch (error: unknown) {
      throw error
    }
  }

  const deleteUser = async (userId: string) => {
    try {
      const response = await Axios.delete<{ status: boolean; detail: string }>(
        `/users/${userId}`
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const resetPassword = async (password: string, token: string) => {
    try {
      const response = await Axios.post<{ status: boolean; detail: string }>(
        '/users/reset-password',
        { password, token }
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const forgotPassword = async (email: string) => {
    try {
      const response = await Axios.post<{ status: boolean; detail: string }>(
        '/users/forgot-password',
        { email }
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const validateToken = async (token: string) => {
    try {
      const response = await Axios.post<{ result: { valid: boolean } }>(
        '/users/reset-password/validate',
        { token }
      )
      return response.data.result
    } catch (error: unknown) {
      return { valid: false }
    }
  }

  const switchTenant = async (companyId: number, roleId: number) => {
    try {
      const response = await Axios.post<GenericResult>(
        `users/switch_tenant/${companyId}/${roleId}`
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  return {
    getUsers,
    createUser,
    updateUser,
    deleteUser,
    switchTenant,
    resetPassword,
    forgotPassword,
    validateToken,
  }
}

export default useUserService
